import { Divider, Radio, Stack, Typography } from '@mui/material';
import {
  type InventoryVolume,
  type GcpDiskSnapshotProperties,
  type AzureDisk,
  type InventorySnapshotVolume,
  Provider,
  type Snapshot,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { Icon } from '@/components/shared/icon';
import { TextDetailsTuple } from '@/components/text/textDetailsTuple';
import { VolumeInfo } from '@/components/volumes/volumeDetails';
import type { BackupVault } from '@/data/vaults/backupVault';

import { SnapshotActionsGroup } from './snapshotActionsGroup';
import type { RestoreActionType } from './vmSnapshotRow';

export type VolumeOrDisk =
  | AzureDisk
  | InventorySnapshotVolume
  | GcpDiskSnapshotProperties;

interface SnapshotDetailsBodyProps {
  resource: InventoryResource;
  snapshot: Snapshot;
  vault?: BackupVault;
  onRestore: (
    volumesOrDisks: VolumeOrDisk[],
    actionType: RestoreActionType
  ) => void;
  onConvertSnapshot?: (
    volumesOrDisks: VolumeOrDisk[],
    actionType: RestoreActionType
  ) => void;
}

export const SnapshotDetailsBody = (props: SnapshotDetailsBodyProps) => {
  const [fullInstanceSelected, setFullInstanceSelected] = useState(true);
  const volumes: VolumeOrDisk[] =
    (props.resource.cloudProvider === Provider.Aws
      ? props.snapshot?.resource?.properties?.awsEc2?.volumes
      : props.resource.cloudProvider === Provider.Azure
        ? props.snapshot?.resource?.properties?.azureVm?.disks
        : props.resource.cloudProvider === Provider.Gcp
          ? props.snapshot?.resource?.properties?.gcpVm?.disks
          : []) || [];

  const [selectedVolume, setSelectedVolume] = useState(volumes[0] || null);

  const onRestore = () => {
    if (fullInstanceSelected) {
      props.onRestore(volumes, 'full-instance');
      return;
    }

    if (selectedVolume) {
      props.onRestore([selectedVolume], 'volume');
    }
  };

  const onConvertSnapshot = () => {
    if (fullInstanceSelected) {
      props.onConvertSnapshot?.(volumes, 'convert-image');
      return;
    }

    if (selectedVolume) {
      props.onConvertSnapshot?.([selectedVolume], 'convert-snapshot');
    }
  };

  return (
    <Stack width='100%' direction='column' marginTop='12px'>
      <Stack
        direction='row'
        gap='40px'
        marginBottom='24px'
        marginLeft='36px'
        divider={<Divider orientation='vertical' flexItem />}
      >
        <Stack direction='column' gap='12px'>
          <Typography noWrap marginBottom='8px' variant='subtitle1'>
            What do you want to restore?
          </Typography>
          <Stack
            className='cursor-pointer'
            direction='row'
            alignItems='center'
            gap='12px'
            onClick={() => setFullInstanceSelected(true)}
          >
            <Radio
              data-testid='full-instance-radio'
              size='small'
              checked={fullInstanceSelected}
            />
            <Typography variant='subtitle1'>Instance</Typography>
          </Stack>
          <Stack
            className='cursor-pointer'
            direction='row'
            alignItems='center'
            gap='12px'
            onClick={() => setFullInstanceSelected(false)}
          >
            <Radio
              data-testid='specific-vol-radio'
              size='small'
              checked={!fullInstanceSelected}
            />
            <Typography variant='subtitle1'>
              {`Specific ${getVolumeProviderTerm(props.resource.cloudProvider)}`}
            </Typography>
          </Stack>
        </Stack>
        <Stack overflow='hidden' flexGrow={1} direction='column' gap='12px'>
          <Typography marginBottom='8px' variant='subtitle1'>
            {fullInstanceSelected
              ? `These ${getVolumeProviderTerm(props.resource.cloudProvider)}s will be restored`
              : `Choose a ${getVolumeProviderTerm(props.resource.cloudProvider)} to restore`}
          </Typography>
          <Stack
            overflow='hidden'
            flexGrow={1}
            direction='column'
            gap='12px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            {volumes.map((volume) => {
              const inventoryVol =
                props.resource.resourceProperties?.awsEc2?.volumes?.find(
                  (v) =>
                    v.providerVolumeId ===
                    (volume as InventorySnapshotVolume).providerVolumeId
                );
              const volName = getVolumeName(
                volume,
                props.resource.cloudProvider,
                inventoryVol
              );
              const volId = getVolumeId(volume, props.resource.cloudProvider);

              return (
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  key={volId}
                  gap='12px'
                >
                  <Stack
                    overflow='hidden'
                    className={
                      fullInstanceSelected ? 'cursor-default' : 'cursor-pointer'
                    }
                    direction='row'
                    alignItems='center'
                    gap='12px'
                    onClick={() => setSelectedVolume(volume)}
                  >
                    {fullInstanceSelected ? (
                      <Icon
                        data-testid='vol-check-icon'
                        className='p-[2px]'
                        iconClass='material-symbols-check w-[20px] h-[20px]'
                      />
                    ) : (
                      <Radio
                        data-testid='vol-selection-radio'
                        size='small'
                        checked={
                          volId ===
                          getVolumeId(
                            selectedVolume,
                            props.resource.cloudProvider
                          )
                        }
                      />
                    )}
                    <TextDetailsTuple
                      primaryProp={volName}
                      secondaryProp={volId}
                    />
                  </Stack>
                  <VolumeInfo
                    volume={volume}
                    cloudProvider={props.resource.cloudProvider}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      <SnapshotActionsGroup
        onRestore={onRestore}
        resource={props.resource}
        isVisible
        convertSnapshot={
          !props.onConvertSnapshot
            ? undefined
            : {
                title: fullInstanceSelected
                  ? 'Convert to AMI'
                  : 'Convert to EBS Snapshot',
                onConvertSnapshot,
              }
        }
      />
    </Stack>
  );
};

const getVolumeProviderTerm = (provider: Provider) => {
  switch (provider) {
    case Provider.Gcp:
    case Provider.Azure:
      return 'disk';
    default:
      return 'volume';
  }
};

const getVolumeName = (
  volume: VolumeOrDisk,
  provider: Provider,
  inventoryVol?: InventoryVolume
) => {
  if (provider === Provider.Aws) {
    const volPath = inventoryVol?.path || '';

    return volPath || (volume as InventorySnapshotVolume).providerVolumeId;
  }

  if (provider === Provider.Gcp) {
    return (
      (volume as GcpDiskSnapshotProperties).diskName ||
      (volume as GcpDiskSnapshotProperties).diskId
    );
  }

  if (provider === Provider.Azure) {
    return (
      (volume as AzureDisk).settings.name ||
      (volume as AzureDisk).providerDiskId
    );
  }
  return '';
};

const getVolumeId = (volume: VolumeOrDisk, provider: Provider) => {
  if (provider === Provider.Aws) {
    return (volume as InventorySnapshotVolume).providerVolumeId;
  }
  if (provider === Provider.Gcp) {
    return (volume as GcpDiskSnapshotProperties).diskId;
  }
  if (provider === Provider.Azure) {
    return (volume as AzureDisk).providerDiskId;
  }
  return '';
};
